import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import useApi from "../../../shared/hooks/useApi";
import { useDebounce } from "../../../shared/hooks/useDebounce";
import {
  components,
  PatientId,
  PatientMedicalDiagnosisCodeId,
} from "../../../shared/schema/schema";
import { IcdCodeAutoCompleteResult } from "../types/medical-diagnosis-code.types";

type MedicalDiagnosisCode = components["schemas"]["PatientMedicalDiagnosisCode"];
type MedicalDiagnosisDesignation = "PRIMARY" | "OTHER";

interface Props {
  isOpen: boolean;
  patientId: PatientId;
  diagnosisCode?: MedicalDiagnosisCode | null;
  onClose: () => void;
  /** After the code was saved (before the modal closes). */
  onSuccess?: () => void;
}

const DESIGNATION_OPTIONS: { id: MedicalDiagnosisDesignation; label: string }[] = [
  { id: "PRIMARY", label: "Primary diagnosis" },
  { id: "OTHER", label: "Other diagnosis" },
];

export default function AddEditMedicalDiagnosisCodeModal({
  isOpen,
  patientId,
  diagnosisCode,
  onClose,
  onSuccess,
}: Props) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const isEditing = diagnosisCode !== null && diagnosisCode !== undefined;

  const [designation, setDesignation] = useState<MedicalDiagnosisDesignation>(
    () => diagnosisCode?.designation ?? "OTHER"
  );
  const [source, setSource] = useState(() => diagnosisCode?.source ?? "");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIcdCode, setSelectedIcdCode] = useState<IcdCodeAutoCompleteResult | null>(() =>
    diagnosisCode
      ? {
          id: diagnosisCode.icdCode,
          dxCode: "",
          formattedDxCode: diagnosisCode.formattedDxCode,
          shortDesc: diagnosisCode.shortDesc,
          longDesc: "",
        }
      : null
  );
  const [icdInputValue, setIcdInputValue] = useState(() => diagnosisCode?.shortDesc ?? "");

  const { isOpen: isPopoverOpen, onOpen: onPopoverOpen, onClose: onPopoverClose } = useDisclosure();
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchTerm = useDebounce(searchTerm.trim(), 300);

  const { data: icdCodes = [], isLoading: isSearching } = useQuery({
    ...queries.patient.searchIcdCodes(debouncedSearchTerm),
    enabled: debouncedSearchTerm.length > 0 && isPopoverOpen,
  });

  const createMutation = useMutation({
    ...mutations.patient.createMedicalDiagnosisCode(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queries.patient.medicalDiagnosisCodes(patientId).queryKey,
      });
      onSuccess?.();
      handleClose();
    },
  });

  const updateMutation = useMutation({
    ...mutations.patient.updateMedicalDiagnosisCode(
      diagnosisCode?.id ?? (0 as PatientMedicalDiagnosisCodeId)
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queries.patient.medicalDiagnosisCodes(patientId).queryKey,
      });
      onSuccess?.();
      handleClose();
    },
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const resetForm = () => {
    setDesignation("OTHER");
    setSource("");
    setSearchTerm("");
    setSelectedIcdCode(null);
    setIcdInputValue("");
  };

  const handleClose = () => {
    if (!isEditing) {
      resetForm();
    }
    onClose();
    onPopoverClose();
  };

  const handleIcdInputChange = (value: string) => {
    setIcdInputValue(value);
    setSearchTerm(value);
    if (value.length > 0) {
      onPopoverOpen();
    } else {
      onPopoverClose();
      setSelectedIcdCode(null);
    }
  };

  const handleIcdCodeSelect = (code: IcdCodeAutoCompleteResult) => {
    setSelectedIcdCode(code);
    setIcdInputValue(code.shortDesc);
    setSearchTerm("");
    onPopoverClose();
  };

  const handleSubmit = () => {
    if (!selectedIcdCode) {
      return;
    }

    if (isEditing && diagnosisCode) {
      updateMutation.mutate({
        designation,
        icdCode: selectedIcdCode.id,
        source: source.trim() === "" ? null : source.trim(),
      });
    } else {
      createMutation.mutate({
        patientId,
        icdCode: selectedIcdCode.id,
        designation,
        source: source.trim() === "" ? null : source.trim(),
      });
    }
  };

  const canSubmit = selectedIcdCode !== null && !isLoading;

  return (
    <Modal isOpen={isOpen} size="lg" onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEditing ? "Edit Medical Diagnosis Code" : "Add Medical Diagnosis Code"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Designation</FormLabel>
              <Select
                value={designation}
                onChange={(e) => setDesignation(e.target.value as MedicalDiagnosisDesignation)}
              >
                {DESIGNATION_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </Select>
              {designation === "PRIMARY" && diagnosisCode?.designation !== "PRIMARY" ? (
                <Text color="gray.600" fontSize="sm" mt={1}>
                  A patient has one primary diagnosis. Saving makes this the primary and changes the
                  current primary, if any, to Other.
                </Text>
              ) : null}
            </FormControl>

            <FormControl>
              <FormLabel>Source</FormLabel>
              <Input
                placeholder="e.g. Provider, Plan"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>ICD Code</FormLabel>
              <Popover
                matchWidth
                isOpen={isPopoverOpen}
                placement="bottom-start"
                onClose={onPopoverClose}
              >
                <PopoverTrigger>
                  <Input
                    ref={inputRef}
                    placeholder="Enter ICD code or description"
                    value={icdInputValue}
                    onChange={(e) => handleIcdInputChange(e.target.value)}
                  />
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverBody p={0}>
                    {isSearching ? (
                      <Box p={4} textAlign="center">
                        <Spinner size="sm" />
                      </Box>
                    ) : icdCodes.length > 0 ? (
                      <List maxH="200px" overflowY="auto">
                        {icdCodes.map((code) => (
                          <ListItem
                            key={code.id}
                            _hover={{ bg: "gray.50" }}
                            cursor="pointer"
                            p={3}
                            onClick={() => handleIcdCodeSelect(code)}
                          >
                            <Text fontSize="sm">
                              {code.formattedDxCode} - {code.shortDesc}
                            </Text>
                          </ListItem>
                        ))}
                      </List>
                    ) : searchTerm.length > 0 ? (
                      <Box color="gray.500" p={4} textAlign="center">
                        No ICD codes found
                      </Box>
                    ) : null}
                  </PopoverBody>
                </PopoverContent>
              </Popover>
              {selectedIcdCode && (
                <Text color="gray.600" fontSize="sm" mt={1}>
                  Selected: {selectedIcdCode.formattedDxCode} - {selectedIcdCode.shortDesc}
                </Text>
              )}
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button isDisabled={isLoading} mr={3} variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!canSubmit}
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            {isEditing ? "Update" : "Save"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
